// KYT v2 · SYSTEM — is the screening itself working: last night's check, the flags and the wallets watched (three
// figures), each provider's own state (its last REAL answer, never a job's clock — a working explorer beside a failed
// oracle is one plain and one red, never one that speaks for both), the rules in force (one line to a level, behind a
// tap) and the flagged wallet checks (the newest twelve of the treasury's own screenings). Today's System view redrawn on
// the kit (the Command Centre, 27 Oct 2026); every figure, word and rule is Kyt.tsx's, imported. No green: a provider that
// answers, a night that ran, a low band are words. The lines about the nightly check and Arkham's credits live here, not
// over the payments (the hand-off's §7.3 and §7.4): they concern the wallets' own scores, checked per night.
import type { KytFlag, KytIncoming, KytOverview } from '../../../lib/api';
import { fmtRelative, fmtStamp } from '../../../lib/format';
import type { ReadState } from '../../../lib/read-state';
import { PROVIDER_WORD, RulesLines, count, nightTile, providerName, providerStrip, senderCheckWhole, senderItem, whole, type StripItem } from '../../../screens/Kyt';
import { Pill } from '../../Pill';
import { Address, EmptyState, Panel, Register, SkeletonRows, StateLine, SummaryStrip, type RegisterColumn } from '../kit';
import { BandTag, CardLine, quiet } from './parts';

const GAP = 'mb-4 lg:mb-5';
const Retry = ({ onClick }: { onClick: () => void }) => <button type="button" className="btn-link text-body" onClick={onClick}>Try again</button>;

/** A provider's state in a word and its tone — the lane's PROVIDER_WORD, or the item's own word for a job (ran, part
 *  done, not running); a working one is plain, never green. */
const stateOf = (it: StripItem) => ({ word: it.word ?? PROVIDER_WORD[it.state].word, tone: quiet(it.tone ?? PROVIDER_WORD[it.state].tone) });
/** What the note says beyond the credits and the time — an item from before the fields (none today) keeps its whole note. */
const noteOf = (it: StripItem): string => (it.credits !== undefined || it.when !== undefined ? it.rest ?? '' : it.note);
const Dash = () => <span className="text-tg-hint">—</span>;
/** One fact a cell (1 Nov 2026): the credits and the time were packed into Note. */
const PROVIDER_COLUMNS: ReadonlyArray<RegisterColumn<StripItem>> = [
  { key: 'name', head: 'Provider', width: 'w-[20%]', cell: (it) => <span className="font-medium">{it.name}</span> },
  { key: 'state', head: 'State', width: 'w-[15%]', cell: (it) => { const s = stateOf(it); return <Pill tone={s.tone}>{s.word}</Pill>; } },
  { key: 'credits', head: 'Credits', width: 'w-[22%]', cell: (it) => (it.credits ? <span className="tabular">{it.credits}</span> : <Dash />) },
  { key: 'when', head: 'When', width: 'w-[17%]', cell: (it) => (it.when ? <span className="text-tg-hint">{it.when}</span> : <Dash />) },
  { key: 'note', head: 'Note', cell: (it) => (noteOf(it) ? <span className="text-tg-hint">{noteOf(it)}</span> : <Dash />) },
];
const providerCard = (it: StripItem) => {
  const s = stateOf(it);
  const note = noteOf(it);
  return {
    title: it.name,
    figure: <Pill tone={s.tone}>{s.word}</Pill>,
    meta: it.credits || it.when || note ? (
      <span className="block whitespace-normal">
        {it.credits && <CardLine label="Credits">{it.credits}</CardLine>}
        {it.when && <CardLine label="When">{it.when}</CardLine>}
        {note && <span className="block">{note}</span>}
      </span>
    ) : undefined,
  };
};

type FlagRow = KytFlag & { k: string };
const FLAG_COLUMNS: ReadonlyArray<RegisterColumn<FlagRow>> = [
  { key: 'when', head: 'When', width: 'w-[12%]', cell: (f) => <span className="whitespace-nowrap text-tg-hint" title={fmtStamp(f.created_at)}>{fmtRelative(f.created_at)}</span> },
  { key: 'chain', head: 'Chain', width: 'w-[10%]', cell: (f) => <span className="font-mono text-meta">{f.chain}</span> },
  { key: 'address', head: 'Address', cell: (f) => <Address value={f.address} /> },
  { key: 'source', head: 'Source', width: 'w-[14%]', cell: (f) => providerName(f.provider) },
  { key: 'band', head: 'Band', width: 'w-[14%]', cell: (f) => <BandTag band={f.risk_band} looked /> },
  { key: 'score', head: 'Score', align: 'right', width: 'w-[10%]', cell: (f) => <span className="font-mono tabular">{f.risk_score ?? '—'}</span> },
];
const flagCard = (f: FlagRow) => ({
  title: <span className="inline-flex items-center gap-x-2"><span className="font-mono text-meta text-tg-hint">{f.chain}</span><Address value={f.address} /></span>,
  figure: <span className="font-mono tabular">{f.risk_score ?? '—'}</span>,
  meta: `${fmtRelative(f.created_at)} · ${providerName(f.provider)}`,
  state: <BandTag band={f.risk_band} looked />,
});

const toneClass = (tone?: 'good' | 'warn' | 'bad'): string => (tone === 'bad' ? 'text-neg' : tone === 'warn' ? 'text-warn' : '');

export function SystemV2({ d, ovState, inc, incFailed, nowMs, onRetry }: {
  d: KytOverview | undefined;
  ovState: ReadState;
  inc: KytIncoming | undefined;
  incFailed: boolean;
  /** The BFF's own read time: a record is aged against it, never against the viewer's clock. */
  nowMs: number;
  onRetry: () => void;
}) {
  if (!d) {
    if (ovState === 'failed') return <Panel title="System" className={GAP}><StateLine tone="bad" action={<Retry onClick={onRetry} />}>Not read.</StateLine></Panel>;
    return <Panel title="System" flush className={GAP}><SkeletonRows rows={3} what="the screener" /></Panel>;
  }
  const strip = providerStrip(d, nowMs);
  const night = nightTile(d, nowMs);
  const senderWhole = senderCheckWhole(senderItem(d, nowMs));
  // "Flags 0" and "Nothing flagged." are claims over what was screened: over a night that looked at a part of the wallets,
  // or a sender check that did not screen every sender, the zero is amber and the list says what it covers.
  const lookedAtAll = (night.tone === 'good' || night.value === 'Nothing to check') && senderWhole;
  const budgetOut = d.spend.arkhamToday >= d.spend.arkhamBudget;
  const stray = d.unwatched_screened ?? [];
  // Newest first, whatever order the worker sent them (the audit, 1 Nov 2026: the list was not newest-first).
  const when = (f: KytFlag): number => { const n = Date.parse(f.created_at); return Number.isFinite(n) ? n : -Infinity; };
  const flags: FlagRow[] = d.flags.map((f, i) => ({ ...f, k: `${f.address}:${f.created_at}:${i}` })).sort((a, b) => when(b) - when(a));
  return (
    <>
      {night.why && !d.meta.alarm && <StateLine tone="bad" className={GAP} more={night.why}>Nothing was checked last night — {night.short}.</StateLine>}
      {budgetOut && !d.meta.alarm && (
        <StateLine tone="warn" className={GAP} more="The nightly check and the transfers read both wait for the day's credits.">Arkham credits spent — checks pause until 04:00 Dubai.</StateLine>
      )}
      {/* The Books stopped watching these; the screener did not hear of it. Said, with the addresses, until it is fixed. */}
      {stray.length > 0 && (
        <div className={GAP}>
          <StateLine tone="warn" more="The Books stopped watching it, and the screener did not hear. Send /unwatch with the address in the ops group.">
            {count(stray.length, 'address', 'addresses')} still screened — send <span className="font-mono">/unwatch</span>.
          </StateLine>
          <ul className="mt-1.5 space-y-1 text-body">
            {stray.map((u) => (
              <li key={`${u.chain}:${u.address}`} className="flex flex-wrap items-center gap-x-2">
                <span className="font-mono text-meta text-tg-hint">{u.chain}</span><Address value={u.address} />{u.label && <span className="text-tg-hint">{u.label}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <SummaryStrip phoneCols={2} items={[
        { label: 'Checked last night', value: <span className={toneClass(night.tone)}>{night.value}</span> },
        { label: 'Flags', value: <span className={d.flags.length || !lookedAtAll ? 'text-warn' : ''}>{whole(d.flags.length)}</span> },
        { label: 'Wallets watched', value: (
          <span className={!d.coverage || d.coverage.watched < d.coverage.treasury ? 'text-warn' : ''}>
            {d.coverage ? `${whole(d.coverage.watched)} of ${whole(d.coverage.treasury)}` : `${whole(d.wallets.length)} of ?`}
          </span>
        ) },
      ]} />
      <Panel title="Providers" flush className={GAP}>
        {strip ? (
          <>
            <Register label="Providers" rows={strip} rowKey={(it) => it.key} columns={PROVIDER_COLUMNS} card={providerCard} />
            {strip.some((it) => it.detail && it.detail !== it.note) && (
              <details className="border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
                <summary className="tap-44 cursor-pointer max-lg:flex max-lg:min-h-11 max-lg:items-center">Provider details</summary>
                <div className="mt-1.5 space-y-1">{strip.filter((it) => it.detail && it.detail !== it.note).map((it) => <p key={it.key}><span className="text-tg-text">{it.name}</span> · {it.detail}</p>)}</div>
              </details>
            )}
          </>
        ) : (
          // A BFF deployed before the strip sends no `providers`: the old hint line stands in for that minute.
          <p className="px-4 py-3 text-meta text-tg-hint lg:px-5">OFAC lists {d.meta.ofac ?? '—'} · Nightly sweep {d.meta.sweep ? fmtRelative(d.meta.sweep) : '—'} · GL Vision {d.spend.gl}{d.spend.glBudget != null && ` of ${d.spend.glBudget}`}</p>
        )}
      </Panel>
      {inc ? (
        <Panel flush className={GAP}>
          <details className="group">
            <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 lg:px-5 [&::-webkit-details-marker]:hidden">
              <span className="text-title font-semibold">The rules in force</span>
              <span className="text-meta text-tg-hint">set with <span className="font-mono">/kytrules</span> · <span className="group-open:hidden">Show</span><span className="hidden group-open:inline">Hide</span></span>
            </summary>
            <div className="border-t border-stroke px-4 py-3 lg:px-5"><RulesLines inc={inc} /></div>
          </details>
        </Panel>
      ) : incFailed && <Panel title="The rules in force" className={GAP}><StateLine tone="warn">Rules not read — the payments read failed.</StateLine></Panel>}
      <Panel title="Flagged wallet checks" aside="newest 12" flush className={GAP}>
        {flags.length === 0
          ? <EmptyState>{lookedAtAll ? 'Nothing flagged.' : 'Nothing flagged in what was checked.'}</EmptyState>
          : <Register label="Flagged wallet checks" rows={flags} rowKey={(f) => f.k} columns={FLAG_COLUMNS} card={flagCard} />}
      </Panel>
    </>
  );
}
